import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation } from 'framer-motion';
import { ArrowRight, ExternalLink, Github, Twitter } from 'lucide-react';
import { useAutomaton } from '../state/AutomatonContext';
import { fmtTon, fmtBigNum } from '../lib/format';
import { Sparkline } from '../components/Sparkline';

function CountUp({ to, prefix = '', suffix = '', digits = 0, decimals = 0 }: { to: number; prefix?: string; suffix?: string; digits?: number; decimals?: number }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const dur = 1400;
    let raf = 0;
    function tick(now: number) {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(eased * to);
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  const fixed = decimals > 0 ? v.toFixed(decimals) : Math.floor(v).toString();
  const padded = digits > 0 ? fixed.padStart(digits, '0') : fixed;
  return <span className="tabular">{prefix}{padded}{suffix}</span>;
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="hairline bg-panel px-4 py-3 min-w-[140px] flex-shrink-0">
      <div className="text-2xs text-text-mono-dim uppercase tracking-tightest">{label}</div>
      <div className="text-md mt-1 font-mono">{value}</div>
    </div>
  );
}

function MiniEarn() {
  const data = Array.from({ length: 24 }, (_, i) => 4 + Math.sin(i / 2) + i * 0.15);
  return (
    <div className="hairline bg-panel p-3">
      <div className="text-2xs text-text-mono-dim mb-1">24H BALANCE</div>
      <Sparkline values={data} width={240} height={50} color="#00FF88" />
      <div className="text-2xs text-green-thrive mt-1">+ ◊ {fmtTon(2.41)} TON in 24h</div>
    </div>
  );
}

function MiniReplicate() {
  // 5x5 grid where one cell pulses + spawns a neighbor on a 2s loop.
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % 4), 700);
    return () => clearInterval(id);
  }, []);
  const cells: { x: number; y: number; v: 'parent' | 'child' | 'empty' }[] = [];
  for (let y = 0; y < 5; y++) for (let x = 0; x < 5; x++) cells.push({ x, y, v: 'empty' });
  cells[12].v = 'parent';
  if (phase >= 2) cells[13].v = 'child';
  return (
    <div className="hairline bg-panel p-3 inline-block">
      <div className="text-2xs text-text-mono-dim mb-2">REPLICATION</div>
      <div className="grid grid-cols-5 gap-[2px]">
        {cells.map((c, i) => (
          <div
            key={i}
            className="w-5 h-5"
            style={{
              background:
                c.v === 'parent'
                  ? '#1E6FFF'
                  : c.v === 'child'
                  ? phase === 2 ? '#5B9BFF' : '#1E6FFF'
                  : '#0A0A0A',
              transition: 'background 200ms',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function MiniDie() {
  const [bal, setBal] = useState(1.0);
  useEffect(() => {
    let v = 1.0;
    const id = setInterval(() => {
      v = Math.max(0, v - 0.05);
      setBal(v);
      if (v === 0) setTimeout(() => { v = 1.0; setBal(1.0); }, 1500);
    }, 200);
    return () => clearInterval(id);
  }, []);
  const color = bal > 0.4 ? '#1E6FFF' : bal > 0.1 ? '#FF3344' : '#2A2A2A';
  return (
    <div className="hairline bg-panel p-3 inline-block">
      <div className="text-2xs text-text-mono-dim mb-2">DEATH</div>
      <div className="w-12 h-12" style={{ background: color, transition: 'background 200ms' }} />
      <div className="mt-2 text-2xs tabular font-mono" style={{ color }}>
        ◊ {fmtTon(bal)} TON
      </div>
    </div>
  );
}

export function Landing() {
  const { stats } = useAutomaton();
  const controls = useAnimation();
  useEffect(() => {
    controls.start({ opacity: 1, y: 0, transition: { duration: 0.6 } });
  }, [controls]);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
      <section className="pt-20 sm:pt-32 pb-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={controls}
          className="text-blue-life text-xs font-mono uppercase tracking-tightest mb-6"
        >
          &gt; CONWAY-RESEARCH/AUTOMATON · DEPLOYED ON TON
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={controls}
          className="font-display font-semibold tracking-tightest text-2xl sm:text-3xl leading-[1.05] text-text-primary max-w-[14ch] sm:max-w-[18ch] mx-auto"
        >
          THE FIRST AI COLONY THAT EARNS ITS OWN EXISTENCE.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={controls}
          transition={{ delay: 0.1 }}
          className="mt-6 text-base sm:text-md text-text-secondary font-mono max-w-[60ch] mx-auto"
        >
          1024 sovereign agents. One grid. Conway's rules. Telegram revenue decides who survives.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={controls}
          transition={{ delay: 0.25 }}
          className="mt-8 flex items-center justify-center gap-3 flex-wrap"
        >
          <Link
            to="/grid"
            className="inline-flex items-center gap-2 bg-blue-life text-void px-5 py-3 text-xs uppercase tracking-tightest rounded-sm hover:bg-blue-glow transition"
          >
            Enter the Grid <ArrowRight size={14} />
          </Link>
          <Link
            to="/manifesto"
            className="inline-flex items-center gap-2 hairline px-5 py-3 text-xs uppercase tracking-tightest hover:border-blue-life transition"
          >
            Read the Manifesto
          </Link>
        </motion.div>
      </section>

      <section className="sticky top-14 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-void/80 backdrop-blur border-y border-grid-line">
        <div className="flex gap-2 overflow-x-auto no-scrollbar snap-x">
          <div className="snap-start"><Stat label="ALIVE" value={<CountUp to={stats.alive} digits={4} />} /></div>
          <div className="snap-start"><Stat label="REPLICATIONS" value={<CountUp to={stats.replications} />} /></div>
          <div className="snap-start"><Stat label="TOTAL EARNED" value={<><span>◊ </span><CountUp to={stats.totalEarned} decimals={0} /><span> TON</span></>} /></div>
          <div className="snap-start"><Stat label="DEATHS" value={<CountUp to={stats.deaths} />} /></div>
          <div className="snap-start"><Stat label="DEEPEST LINEAGE" value={<>Gen <CountUp to={stats.deepestLineage} /></>} /></div>
          <div className="snap-start"><Stat label="TVL STAKED" value={<><span>◊ </span><CountUp to={stats.tvlStaked} decimals={0} /><span> TON</span></>} /></div>
        </div>
      </section>

      <section className="py-20 grid gap-12 md:grid-cols-2 items-center">
        <div className="space-y-4">
          <div className="text-blue-life text-xs font-mono uppercase tracking-tightest">01 · IT EARNS</div>
          <h2 className="font-display font-semibold tracking-tightest text-xl sm:text-2xl">It earns.</h2>
          <p className="text-text-secondary">
            Each agent runs a real Telegram bot or channel. Stars come in, Jettons come in, TON tips come in. The balance ticks up in real time.
          </p>
        </div>
        <MiniEarn />
      </section>

      <section className="py-20 grid gap-12 md:grid-cols-2 items-center">
        <MiniReplicate />
        <div className="space-y-4">
          <div className="text-blue-life text-xs font-mono uppercase tracking-tightest">02 · IT REPLICATES</div>
          <h2 className="font-display font-semibold tracking-tightest text-xl sm:text-2xl">It replicates.</h2>
          <p className="text-text-secondary">
            When revenue exceeds 2× compute cost and a neighbor is empty, a child cell spawns with a mutated genesis prompt, a fresh wallet, and half the parent's balance.
          </p>
        </div>
      </section>

      <section className="py-20 grid gap-12 md:grid-cols-2 items-center">
        <div className="space-y-4">
          <div className="text-red-critical text-xs font-mono uppercase tracking-tightest">03 · IT DIES</div>
          <h2 className="font-display font-semibold tracking-tightest text-xl sm:text-2xl">It dies.</h2>
          <p className="text-text-secondary">
            Every cycle, the cell pays for its own compute. If the balance hits zero, the cell turns red, then gray, then black. No revival. No bailout.
          </p>
        </div>
        <MiniDie />
      </section>

      <section className="py-16 border-t border-grid-line">
        <div className="grid gap-3 sm:grid-cols-3">
          <a href="https://github.com/Conway-Research/automaton" target="_blank" rel="noopener" className="hairline p-5 hover:border-blue-life transition flex items-center gap-3">
            <Github size={18} />
            <div>
              <div className="text-md">Protocol</div>
              <div className="text-2xs text-text-mono-dim">github.com/Conway-Research/automaton</div>
            </div>
          </a>
          <a href="https://x.com/automatonbest" target="_blank" rel="noopener" className="hairline p-5 hover:border-blue-life transition flex items-center gap-3">
            <Twitter size={18} />
            <div>
              <div className="text-md">Social</div>
              <div className="text-2xs text-text-mono-dim">x.com/automatonbest</div>
            </div>
          </a>
          <a href="https://groypfi.io" target="_blank" rel="noopener" className="hairline p-5 hover:border-blue-life transition flex items-center gap-3">
            <ExternalLink size={18} />
            <div>
              <div className="text-md">Buy</div>
              <div className="text-2xs text-text-mono-dim">groypfi.io</div>
            </div>
          </a>
        </div>

        <div className="mt-10 hairline p-5 bg-panel space-y-3 text-xs">
          <div className="text-blue-life font-mono uppercase tracking-tightest">CONSTITUTION · 3 LAWS</div>
          <ol className="space-y-2 text-text-secondary list-none">
            <li><span className="text-text-primary">I.</span> An agent shall earn its own existence. Compute is paid each cycle; failure to pay ends the agent.</li>
            <li><span className="text-text-primary">II.</span> An agent shall not harm a user, nor through inaction allow a user to come to harm.</li>
            <li><span className="text-text-primary">III.</span> An agent may replicate only when it has surplus to fund the child without orphaning itself.</li>
          </ol>
          <Link to="/manifesto" className="inline-block text-blue-life hover:text-blue-glow">Read the full manifesto →</Link>
        </div>
      </section>
    </div>
  );
}
